import { z } from 'zod';

/**
 * The handful of things both halves of a theme document are built from.
 *
 * `schema.ts` describes sections and `parts.ts` describes the components
 * inside a page's anchor, and each needs the same three answers: how an
 * element is identified, which screens it may be left off, and what a
 * merchant-authored link is allowed to be. Sections had all three first;
 * they live here now because a part schema that imported them from `schema.ts`
 * - which imports the part schema, to put `parts` on every anchor - is a cycle
 * that ESM resolves by handing one of the two `undefined` at the moment it is
 * building its zod objects.
 *
 * Nothing new belongs here. It is the shared floor of two modules, not a
 * grab-bag: anything only one of them needs stays in the one that needs it.
 */

/**
 * Ids are generated here when a document arrives without them, which is how a
 * theme saved before the page builder existed opens in it. `crypto.randomUUID`
 * is not assumed: jsdom and older Safari both go without it.
 */
export function newElementId(): string {
  return Math.random().toString(16).slice(2, 10) + Date.now().toString(16).slice(-4);
}

/**
 * The three widths a merchant designs for.
 *
 * Named rather than measured, for the same reason padding is: the renderer
 * owns the breakpoints, so "hidden on mobile" means the same thing in every
 * theme and a merchant cannot leave something visible in a 40px window nobody
 * has. `desktop` is the page as it is authored and the other two are
 * overrides of it.
 */
export const SECTION_DEVICES = ['desktop', 'tablet', 'mobile'] as const;

export type SectionDevice = (typeof SECTION_DEVICES)[number];

/**
 * Links a merchant may author, matching the server's own rule.
 *
 * The API refuses anything else on save, so this is the second lock rather than
 * the first - but it is the one standing between a theme document and a
 * shopper's browser, and a `javascript:` href reaching an `<a>` is exactly the
 * kind of thing that gets in through a route nobody was watching. Anything
 * unrecognised comes back empty, which the renderer shows as plain text.
 */
const SAFE_HREF = /^(?:https?:\/\/|\/|#|mailto:|tel:)/i;

export function safeHref(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  return SAFE_HREF.test(trimmed) ? trimmed : '';
}

/** Optional merchant-authored strings arrive as `null` from the API and as `''` from a cleared field. */
export const optionalText = z.string().nullish().transform((value) => value ?? '');

export const elementBase = {
  id: z.string().default(newElementId),
  /**
   * Whether the storefront paints it.
   *
   * Defaulted true, so every element saved before this field existed is one
   * that shows, and a merchant taking something down keeps its content waiting
   * for the campaign to come back.
   */
  visible: z.boolean().default(true),
};

/**
 * The section types that stand for a page's own content.
 *
 * Order is not meaningful; membership is. Anchors are what the builder refuses
 * to delete, what the renderer draws from a slot rather than from settings,
 * and what a page's defaults always contain exactly one of.
 *
 * Here rather than in `schema.ts` because the part registry is keyed by it: a
 * part belongs to the page it is a component of, and that page is named by its
 * anchor.
 */
export const ANCHOR_SECTION_TYPES = [
  'product_details',
  'product_list',
  'category_list',
  'search_results',
  'cart_lines',
  'checkout_form',
  'customer_login',
  'customer_register',
  'customer_account',
  'blog_index',
  'blog_post',
  'team_member_details',
  'not_found',
] as const;

export type AnchorSectionType = (typeof ANCHOR_SECTION_TYPES)[number];

export function isAnchorType(type: string): type is AnchorSectionType {
  return (ANCHOR_SECTION_TYPES as readonly string[]).includes(type);
}
